import axios, { useZionAxios } from '@fs/zion-axios'

const llsServiceUrl = '/service/gen/llsc'
const myTreesServiceUrl = '/service/tree/mytrees'
const myTreesURL = `${myTreesServiceUrl}/tree`
const myTreesPersonURL = `${myTreesServiceUrl}/person`
const myTreesMatchedPersonURL = `${myTreesPersonURL}/matched`
const myTreesMatchLevelURL = `${myTreesServiceUrl}/match`
const myTreesUpdatePersonURL = `${myTreesPersonURL}/update`
const tfParentsURL = '/service/tree/tree-data/r9/family-members/person/'

const TIMEOUT_DURATIONS = {
  '10sec': 10000,
  '20sec': 20000,
  '30sec': 30000,
  '5min': 300000,
}

class RequiredParamError extends Error {
  constructor(missingField) {
    super(`[my-trees-service] Missing required parameter: ${missingField}`)
    this.name = 'RequiredParamError'
  }
}

function errorHandler(error) {
  // If the user cancels the upload, don't do anything
  if (axios.isCancel(error)) {
    return
  }
  throw error
}

export function addPerson(sessionId, personUri) {
  if (!sessionId) return Promise.reject(new RequiredParamError('sessionId'))
  if (!personUri) return Promise.reject(new RequiredParamError('personUri'))

  const params = new URLSearchParams()
  params.append('personUri', personUri)
  params.append('sessionId', sessionId)

  return axios
    .post(myTreesPersonURL, params, {
      timeout: TIMEOUT_DURATIONS['30sec'],
    })
    .then((res) => {
      return res.data
    })
    .catch((error) => {
      errorHandler(error.response)
    })
}

export function compareTree(sessionId, treeUri) {
  if (!sessionId) return Promise.reject(new RequiredParamError('sessionId'))
  if (!treeUri) return Promise.reject(new RequiredParamError('treeUri'))

  return axios
    .post(
      `${myTreesURL}?action=compare&sessionId=${encodeURIComponent(sessionId)}&treeUri=${encodeURIComponent(treeUri)}`,
      null,
      {
        timeout: TIMEOUT_DURATIONS['5min'],
      }
    )
    .catch(errorHandler)
}

export function deleteTree(sessionId, treeUri) {
  if (!sessionId) return Promise.reject(new RequiredParamError('sessionId'))
  if (!treeUri) return Promise.reject(new RequiredParamError('treeUri'))

  return axios
    .delete(`${myTreesURL}?sessionId=${sessionId}&treeUri=${encodeURIComponent(treeUri)}`, {
      timeout: TIMEOUT_DURATIONS['30sec'],
    })
    .catch(errorHandler)
}

export function getPersonData(personUri, sessionId) {
  return axios
    .get(`${myTreesMatchedPersonURL}?sessionId=${sessionId}&personUri=${encodeURIComponent(personUri)}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      timeout: TIMEOUT_DURATIONS['30sec'],
    })
    .then((res) => res.data)
    .catch(errorHandler)
}

export function getPersons(sessionId, treeId, start, size, category) {
  const realStart = start ?? 0
  const realSize = size ?? 10
  if (!sessionId) return Promise.reject(new RequiredParamError('sessionId'))
  if (!treeId) return Promise.reject(new RequiredParamError('treeId'))
  if (!realStart && realStart !== 0) return Promise.reject(new RequiredParamError('start'))
  if (!realSize) return Promise.reject(new RequiredParamError('size'))
  if (!category && category !== 0) return Promise.reject(new RequiredParamError('category'))

  return axios
    .get(`${myTreesURL}/${treeId}/persons`, {
      params: {
        sessionId,
        start: realStart,
        size: realSize,
        category,
        summary: false,
      },
      timeout: TIMEOUT_DURATIONS['30sec'],
    })
    .then((res) => res.data)
    .catch(errorHandler)
}

export function getTreePersonData(pid) {
  return axios
    .get(`${tfParentsURL}${pid}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      timeout: TIMEOUT_DURATIONS['30sec'],
    })
    .then((res) => res.data)
    .catch(errorHandler)
}

export function getTree(sessionId, treeId) {
  if (!sessionId) return Promise.reject(new RequiredParamError('sessionId'))
  if (!treeId) return Promise.reject(new RequiredParamError('treeId'))
  const url = `${myTreesURL}/${treeId}`

  return axios
    .get(`${url}?sessionId=${sessionId}`, { timeout: TIMEOUT_DURATIONS['20sec'] })
    .then((res) => res.data)
    .catch(errorHandler)
}

export function getTrees(sessionId) {
  if (!sessionId) return Promise.reject(new RequiredParamError('sessionId'))

  return axios
    .get(myTreesURL, {
      params: { sessionId },
      timeout: TIMEOUT_DURATIONS['30sec'],
    })
    .catch(errorHandler)
}

/* istanbul ignore next -- React hook, tested via component */
export function useTrees(sessionId) {
  return useZionAxios(`${myTreesURL}?sessionId=${sessionId}`, { timeout: TIMEOUT_DURATIONS['30sec'] })
}

export function getTreeSummary(sessionId, treeId) {
  if (!sessionId) return Promise.reject(new RequiredParamError('sessionId'))
  if (!treeId) return Promise.reject(new RequiredParamError('treeId'))
  return axios
    .get(`${myTreesURL}/${treeId}/persons`, {
      params: {
        sessionId,
        start: 0,
        size: 1,
        category: 4,
        summary: true,
      },
      timeout: TIMEOUT_DURATIONS['30sec'],
    })
    .then((res) => res.data)
    .catch(errorHandler)
}

export function mergeConclusions(sessionId, ctPersonId, conclusions) {
  if (!sessionId) return Promise.reject(new RequiredParamError('sessionId'))
  if (!ctPersonId) return Promise.reject(new RequiredParamError('ctPersonId'))
  if (!conclusions) return Promise.reject(new RequiredParamError('conclusions'))
  const params = `sessionId=${sessionId}&ctPersonId=${ctPersonId}`
  return axios
    .post(`${myTreesUpdatePersonURL}?${params}`, conclusions, {
      timeout: TIMEOUT_DURATIONS['30sec'],
    })
    .catch(errorHandler)
}

export function updateMatch(sessionId, matchId, matchData) {
  if (!sessionId) return Promise.reject(new RequiredParamError('sessionId'))
  if (!matchId) return Promise.reject(new RequiredParamError('matchId'))
  if (!matchData) return Promise.reject(new RequiredParamError('matchData'))
  const url = `${myTreesMatchLevelURL}/${encodeURIComponent(matchId)}`
  const params = `sessionId=${sessionId}&matchLevel=${matchData.matchLevel}`

  return axios
    .put(`${url}?${params}`, {
      data: matchData,
      timeout: TIMEOUT_DURATIONS['30sec'],
    })
    .catch(errorHandler)
}

export function uploadTree(
  sessionId,
  treeFile,
  treeName,
  treeDescription,
  collectionId,
  onUploadProgress,
  cancelToken
) {
  if (!sessionId) return Promise.reject(new RequiredParamError('sessionId'))
  if (!treeFile) return Promise.reject(new RequiredParamError('treeFile'))
  const url = collectionId ? `${llsServiceUrl}/collection/${collectionId}/contents` : `${llsServiceUrl}/gedcom`
  let progress = 0

  return axios
    .post(url, treeFile, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      onUploadProgress: (uploadValues) => {
        const { loaded, total } = uploadValues
        progress = loaded / total
        onUploadProgress(uploadValues)
      },
      cancelToken,
      params: {
        sessionId,
        filename: treeName || treeFile.name,
        description: treeDescription || undefined,
      },
      timeout: TIMEOUT_DURATIONS['5min'],
    })
    .catch((error) => {
      // THERE IS A FALSE NEGATIVE ERROR that happens when you upload a large file and it takes a while to load.
      // It shows an "ERR_EMPTY_RESPONSE", but the file actually succeeds in uploading. It's not clear why this happens.
      // This check makes it fail graceful when that specific error occurs.
      if (error.isAxiosError && error.message === 'Network Error' && progress === 1) {
        return
      }

      errorHandler(error)
    })
}

export function getCollections(sessionId) {
  return axios
    .get(`${myTreesServiceUrl}/collections?sessionId=${sessionId}`, {
      timeout: TIMEOUT_DURATIONS['30sec'],
    })
    .then(({ data }) => data)
    .catch(errorHandler)
}

export function recomparePerson(sessionId, personUri) {
  return axios
    .put(
      `${myTreesServiceUrl}/person/recompare?personUri=${encodeURIComponent(personUri)}&sessionId=${encodeURIComponent(
        sessionId
      )}`,
      {
        timeout: TIMEOUT_DURATIONS['30sec'],
      }
    )
    .then(({ data }) => data)
    .catch(errorHandler)
}
